import type { Node } from '../../../../core/models/scene';
import { getTableCellContentRect, getTableCellStyleFromAttrs } from './tableCellSpacing.utils';

type Point = { x: number; y: number };
type Rect = { x: number; y: number; w: number; h: number };

const MIN_CELL = 20;

const clampCount = (value: unknown, fallback: number, min = 1): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.max(min, Math.floor(value));
  }
  return fallback;
};

const ensureArray = (arr: unknown, count: number, fallbackTotal: number): number[] => {
  if (Array.isArray(arr) && arr.length === count && arr.every((n) => typeof n === 'number' && Number.isFinite(n) && n > 0)) {
    return arr as number[];
  }
  const even = Math.max(MIN_CELL, fallbackTotal / Math.max(1, count));
  return Array(count).fill(even);
};

export type TableDropHit = {
  tableId: string;
  cellKey: `${number},${number}`;
  cellRect: Rect; // absolute diagram coords
  contentRect: Rect; // absolute diagram coords after cell margin/inset
};

type MergedRegion = { startRow: number; startCol: number; endRow: number; endCol: number };

const containsCell = (r: MergedRegion, row: number, col: number) => {
  return row >= r.startRow && row <= r.endRow && col >= r.startCol && col <= r.endCol;
};

const parseCellKey = (key: `${number},${number}`) => {
  const [rStr, cStr] = String(key).split(',');
  return { r: Number(rStr), c: Number(cStr) };
};

const buildStarts = (arr: number[]) => {
  const acc: number[] = [0];
  for (let i = 0; i < arr.length; i += 1) acc.push(acc[i] + arr[i]);
  return acc;
};

export const getTableCellRectByKey = (tableNode: Node, cellKey: `${number},${number}`): TableDropHit | null => {
  const x0 = (tableNode.x ?? 0) as number;
  const y0 = (tableNode.y ?? 0) as number;
  const w0 = ((tableNode.width ?? tableNode.w ?? 240) as number) || 240;
  const h0 = ((tableNode.height ?? tableNode.h ?? 180) as number) || 180;
  if (!Number.isFinite(x0) || !Number.isFinite(y0) || !Number.isFinite(w0) || !Number.isFinite(h0)) return null;

  const attrs = (tableNode.attrs ?? {}) as Record<string, unknown>;
  const rowCount = clampCount(attrs.rowCount, 3);
  const colCount = clampCount(attrs.columnCount, 3);
  const showHeader = attrs.showHeader !== false;
  const headerHeight =
    typeof attrs.headerHeight === 'number' && Number.isFinite(attrs.headerHeight) && attrs.headerHeight > 0
      ? Math.max(MIN_CELL, attrs.headerHeight)
      : 32;

  const bodyHeight = Math.max(MIN_CELL * rowCount, h0 - (showHeader ? headerHeight : 0));
  const rowHeights = ensureArray(attrs.rowHeights, rowCount, bodyHeight);
  const colWidths = ensureArray(attrs.columnWidths, colCount, w0);
  const combinedHeights = showHeader ? [headerHeight, ...rowHeights] : [...rowHeights];

  const { r, c } = parseCellKey(cellKey);
  const rSafe = Math.max(0, Math.min(combinedHeights.length - 1, r));
  const cSafe = Math.max(0, Math.min(colCount - 1, c));

  const mergedRegions = (Array.isArray((attrs as any).mergedRegions) ? ((attrs as any).mergedRegions as MergedRegion[]) : []) ?? [];
  const mr = mergedRegions.find((m) => containsCell(m, rSafe, cSafe)) ?? null;
  const anchor = mr ? { r: mr.startRow, c: mr.startCol } : { r: rSafe, c: cSafe };
  const span = mr ? { r2: mr.endRow, c2: mr.endCol } : { r2: rSafe, c2: cSafe };

  const rowStarts = buildStarts(combinedHeights);
  const colStarts = buildStarts(colWidths);

  const x1 = x0 + (colStarts[anchor.c] ?? 0);
  const y1 = y0 + (rowStarts[anchor.r] ?? 0);
  const x2 = x0 + (colStarts[span.c2 + 1] ?? w0);
  const y2 = y0 + (rowStarts[span.r2 + 1] ?? h0);
  const cellRect = { x: x1, y: y1, w: Math.max(0, x2 - x1), h: Math.max(0, y2 - y1) };
  const style = getTableCellStyleFromAttrs(attrs, `${anchor.r},${anchor.c}`);
  const contentRect = getTableCellContentRect(cellRect, style);

  return {
    tableId: tableNode.id,
    cellKey: `${anchor.r},${anchor.c}`,
    cellRect,
    contentRect,
  };
};

export const getTableDropHitAtPoint = (tableNode: Node, diagramPoint: Point): TableDropHit | null => {
  const x0 = (tableNode.x ?? 0) as number;
  const y0 = (tableNode.y ?? 0) as number;
  const w0 = ((tableNode.width ?? tableNode.w ?? 240) as number) || 240;
  const h0 = ((tableNode.height ?? tableNode.h ?? 180) as number) || 180;
  if (!Number.isFinite(x0) || !Number.isFinite(y0) || !Number.isFinite(w0) || !Number.isFinite(h0)) return null;

  if (diagramPoint.x < x0 || diagramPoint.y < y0 || diagramPoint.x > x0 + w0 || diagramPoint.y > y0 + h0) return null;

  const attrs = (tableNode.attrs ?? {}) as Record<string, unknown>;
  const rowCount = clampCount(attrs.rowCount, 3);
  const colCount = clampCount(attrs.columnCount, 3);
  const showHeader = attrs.showHeader !== false;
  const headerHeight =
    typeof attrs.headerHeight === 'number' && Number.isFinite(attrs.headerHeight) && attrs.headerHeight > 0
      ? Math.max(MIN_CELL, attrs.headerHeight)
      : 32;

  const bodyHeight = Math.max(MIN_CELL * rowCount, h0 - (showHeader ? headerHeight : 0));
  const rowHeights = ensureArray(attrs.rowHeights, rowCount, bodyHeight);
  const colWidths = ensureArray(attrs.columnWidths, colCount, w0);

  const combinedHeights = showHeader ? [headerHeight, ...rowHeights] : [...rowHeights];

  const relX = Math.min(w0 - 0.0001, Math.max(0, diagramPoint.x - x0));
  const relY = Math.min(h0 - 0.0001, Math.max(0, diagramPoint.y - y0));

  let c = colCount - 1;
  let accX = 0;
  for (let i = 0; i < colCount; i += 1) {
    const next = accX + colWidths[i];
    if (relX < next) {
      c = i;
      break;
    }
    accX = next;
  }

  let r = combinedHeights.length - 1;
  let accY = 0;
  for (let i = 0; i < combinedHeights.length; i += 1) {
    const next = accY + combinedHeights[i];
    if (relY < next) {
      r = i;
      break;
    }
    accY = next;
  }

  return getTableCellRectByKey(tableNode, `${r},${c}`);
};
